import { useApolloClient } from '@apollo/client';
import { Form, message } from 'antd';
import { Formik } from 'formik';
import { pick } from 'lodash/fp';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { type RoleSpecsFragment } from '../../../api/fragments/RoleSpecs';
import {
    UpdateRoleDocument,
    type UpdateRoleMutation,
    type UpdateRoleMutationVariables,
} from '../../../api/mutations/updateRole';
import { type RoleSettings } from '../../../api/types';
import InputField from '../../../components/fields/InputField';
import SwitchField from '../../../components/fields/SwitchField';
import useHandleError from '../../../utilities/useHandleError';
import useSystemSwitchData from '../../../utilities/useSystemSwitchData';
import useValidator from '../../../utilities/useValidator';
import validators from '../../../utilities/validators';

export type RoleUpdateFormProps = {
    role: RoleSpecsFragment;
    onComplete: () => void;
};

type FormValues = RoleSettings;

const formValidator = validators.compose(
    validators.requiredString('displayName'),
    validators.requiredString('description')
);

const RoleUpdateForm = ({ role, onComplete }: RoleUpdateFormProps) => {
    const { t } = useTranslation('roleDetails');
    const apolloClient = useApolloClient();

    const { yesNoSwitch } = useSystemSwitchData();

    const roleId = role.id;

    const validate = useValidator(formValidator);

    const onSubmit = useHandleError<FormValues>(
        async values => {
            message.loading({
                content: t('roleDetails:updateDrawer.messages.updateSubmitting'),
                key: 'primary',
                duration: 0,
            });

            // submit update
            await apolloClient
                .mutate<UpdateRoleMutation, UpdateRoleMutationVariables>({
                    mutation: UpdateRoleDocument,
                    variables: { roleId, settings: values },
                })
                .finally(() => {
                    message.destroy('primary');
                });

            message.success({
                content: t('roleDetails:updateDrawer.messages.updateSuccessful'),
                key: 'primary',
            });

            onComplete();
        },
        [onComplete, apolloClient, t, roleId]
    );

    const initialValues = useMemo(
        (): FormValues => ({
            ...pick(['displayName', 'description', 'isActive'], role),
        }),
        [role]
    );

    return (
        <Formik initialValues={initialValues} onSubmit={onSubmit} validate={validate}>
            {({ handleSubmit }) => (
                <Form id="updateRole" layout="vertical" name="updateRole" onSubmitCapture={handleSubmit}>
                    <InputField
                        {...t('roleDetails:updateDrawer.displayName', { returnObjects: true })}
                        name="displayName"
                    />
                    <InputField
                        {...t('roleDetails:updateDrawer.description', { returnObjects: true })}
                        name="description"
                    />
                    <SwitchField
                        {...t('roleDetails:updateDrawer.isActive', { returnObjects: true })}
                        name="isActive"
                        {...yesNoSwitch}
                    />
                </Form>
            )}
        </Formik>
    );
};

export default RoleUpdateForm;
